import { formatCurrency } from "../number-format.utils";

/**
 * Language of an exported document (quote/contract `.docx`).
 * `it` is the historical default; `en` enables the English export flow
 * (translated static template texts + AWS-translated user free text).
 */
export type DocumentLanguage = "it" | "en";

/**
 * Intl locale used for date/number formatting, per document language.
 * Currency is always EUR — only the formatting locale changes.
 */
const DOCUMENT_LOCALES: Record<DocumentLanguage, string> = {
  it: "it-IT",
  en: "en-GB",
};

/**
 * Formats a currency value (always EUR) in the plain Intl locale style of the
 * document language (e.g. `1.234,56 €` for Italian, `€1,234.56` for English).
 *
 * Used by the custom-template variable builders, whose Italian output has
 * historically used this symbol-after style — do NOT use for the default
 * hardcoded templates (see {@link formatDocumentCurrency}).
 *
 * @param value - Numeric value to format.
 * @param language - Target document language.
 * @returns Locale-formatted EUR currency string.
 */
export function formatLocaleCurrency(
  value: number,
  language: DocumentLanguage,
): string {
  return (value || 0).toLocaleString(DOCUMENT_LOCALES[language], {
    style: "currency",
    currency: "EUR",
  });
}

/**
 * Formats a currency value (always EUR) for the default hardcoded templates.
 *
 * Italian keeps the historical `€ 1.234,56` symbol-first format produced by
 * the shared `formatCurrency` util, so Italian documents stay byte-identical
 * to those generated before the multilingual export (and consistent with the
 * in-app UI). English uses the `en-GB` Intl style (`€1,234.56`).
 *
 * @param value - Numeric value to format.
 * @param language - Target document language.
 * @returns Formatted EUR currency string.
 */
export function formatDocumentCurrency(
  value: number,
  language: DocumentLanguage,
): string {
  if (language === "it") {
    return formatCurrency(value || 0);
  }
  return formatLocaleCurrency(value, language);
}

/**
 * Formats a date in the numeric `DD/MM/YYYY` style used across exported
 * documents, according to the document language locale.
 *
 * @param date - Date to format.
 * @param language - Target document language.
 * @returns Locale-formatted date string.
 */
export function formatDocumentDate(
  date: Date,
  language: DocumentLanguage,
): string {
  return date.toLocaleDateString(DOCUMENT_LOCALES[language], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
